import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Favicon from 'react-favicon';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';

import LoginLayout from 'layout/LoginLayout';

import Row from 'components/Layout/Row';
import Logo from 'components/Logo';
import GoogleSignInButton from 'components/GoogleSignInButton';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Head from 'components/Head';
import Loading from 'components/Loading';

import LoginForm from 'modules/Users/components/Login/LoginForm';

import { auth, db } from 'firebaseConfig';

import { signInRequest, signupUpdate } from 'modules/Users/store/actions';

import { convertGSToDownloadURL } from 'utils';

const LoginPage = ({ dispatch, loading, error, signUpError, searchParams }) => {
  const [isSumoLing, setSumoLing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [processing, setProcessing] = useState(true);
  const [privilegeSuccessModal, setPrivilegeSuccessModal] = useState(false);
  const [favicon, setFavicon] = useState('');

  const navigate = useNavigate();

  const loginUser = (payload) => {
    dispatch(signInRequest(payload, navigate));
  };

  const googleClickHandler = (e) => {
    e.target.disabled = true;
    window.location.href = `${
      process.env.REACT_APP_ENV === 'production'
        ? 'https://app.ruttl.com'
        : 'https://staging.ruttl.com'
    }/auth/google?redirect_url=${window.location.origin}`;
  };

  const closePrivilegeSuccessModal = () => {
    setPrivilegeSuccessModal(false);
    navigate(
      {
        search: '',
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (signUpError && document.getElementById('google-sign-in'))
      document.getElementById('google-sign-in').disabled = false;
  }, [signUpError]);

  useEffect(() => {
    dispatch(signupUpdate({ error: {} }));
  }, [dispatch]);

  useEffect(() => {
    const searchEmail = new URL(
      `https://app.ruttl.com/${searchParams}`,
    ).searchParams.get('email');
    const isSumo = new URL(
      `https://app.ruttl.com/${searchParams}`,
    ).searchParams.get('sumo');
    const isDelete = new URL(
      `https://app.ruttl.com/${searchParams}`,
    ).searchParams.get('delete');
    const privilegeSuccess = new URL(
      `https://app.ruttl.com/${searchParams}`,
    ).searchParams.get('privilege-success');

    if (isDelete === 'true') {
      setConfirmDelete(true);
      setPrivilegeSuccessModal(false);
    } else if (privilegeSuccess) {
      setConfirmDelete(false);
      setPrivilegeSuccessModal(true);
    } else if (searchEmail && searchEmail.length > 0 && isSumo) {
      setSumoLing(searchEmail);
      setPrivilegeSuccessModal(false);
      navigate(window.location.pathname, { replace: true });
      setConfirmDelete(false);
    } else {
      setConfirmDelete(false);
      setPrivilegeSuccessModal(false);
    }
  }, [searchParams, navigate, dispatch]);

  useEffect(() => {
    const { searchParams: search } = new URL(window.location.href);
    const token = search.get('token');

    if (token) {
      const isNewUser = search.get('new');

      (async () => {
        try {
          const {
            user: { uid },
          } = await signInWithCustomToken(auth, token);
          navigate(
            {
              search: '',
              new: '',
            },
            { replace: true },
          );

          if (isNewUser === 'true') {
            if (window.fbq) window.fbq('trackCustom', 'SignUp');
            if (window.dataLayer)
              window.dataLayer.push({
                event: 'app-new-signup',
                uid: uid || '',
              });
          }
        } catch (e) {
          /* do nothing */
        } finally {
          setProcessing(false);
        }
      })();
    } else setProcessing(false);
  }, [navigate]);

  useEffect(() => {
    if (
      window.location.origin !== 'https://app.ruttl.com' ||
      window.location.origin !== 'https://staging.ruttl.com'
    ) {
      (async () => {
        try {
          const { docs } = await getDocs(
            query(
              collection(db, 'teams'),
              where('domain', '==', window.location.hostname),
            ),
          );

          if (docs.length) {
            const snapshot = docs[0];

            const { customBranding, isDisabled, theme } = snapshot.data();

            if (isDisabled === false && customBranding && theme.iconURL) {
              if (theme.iconURL.startsWith('gs://')) {
                setFavicon(convertGSToDownloadURL(theme.iconURL, false));
              }
            }
          }
        } catch (e) {
          /* do nothing */
        }
      })();
    }
  }, []);

  return (
    <>
      {favicon ? <Favicon url={favicon} /> : null}
      <Head />
      {processing ? (
        <Loading />
      ) : (
        <LoginLayout>
          <Row justify="space-between" style={{ margin: 0 }}>
            {window.location.origin === 'https://app.ruttl.com' ||
            window.location.origin === 'https://staging.ruttl.com' ? (
              <a
                href="https://ruttl.com/"
                target="_blank"
                rel="noopener noreferrer">
                <Logo />
              </a>
            ) : (
              <div />
            )}
            {!isSumoLing && !confirmDelete && (
              <Link to="/signup" className="link">
                Sign up
              </Link>
            )}
          </Row>
          <h2>
            {confirmDelete
              ? 'Sign in to delete account'
              : `Sign in${isSumoLing ? ' to finish activation' : ''}`}
          </h2>
          {!isSumoLing && (
            <>
              <GoogleSignInButton clickHandler={googleClickHandler} />
              <p className="or-use-text">Or use your email to sign in:</p>
            </>
          )}
          <LoginForm
            loginUser={loginUser}
            loading={loading}
            error={
              (Object.keys(error).length > 0 && error) ||
              (Object.keys(signUpError).length > 0 && signUpError) ||
              {}
            }
            isSumoLing={isSumoLing}
          />
        </LoginLayout>
      )}
      <Modal
        show={privilegeSuccessModal}
        toggleModal={closePrivilegeSuccessModal}
        contentProps={{ style: { padding: 0 } }}
        closeButtonProps={{ style: { fill: '#fff' } }}>
        <img
          src="/assets/img/ruttl-collaboration-header.png"
          alt="ruttl collaboration"
        />
        <div className="center" style={{ padding: 30 }}>
          <h3 style={{ fontSize: 24, margin: 0 }}>
            Congratulations on purchasing our lifetime privilege plan!
          </h3>
          <p>
            Please login or{' '}
            <Link to="/signup" className="link">
              signup
            </Link>{' '}
            (if you are a new user) to continue.
          </p>
          <Button
            type="button"
            styled
            onClick={closePrivilegeSuccessModal}
            style={{ padding: '14px 40px', fontSize: 16, marginTop: 10 }}>
            Get started
          </Button>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = ({
  user: {
    loading,
    error,
    signup: { error: signUpError },
  },
  app: { searchParams },
}) => ({ loading, error, signUpError, searchParams });

const mapDispatchToProps = (dispatch) => ({ dispatch });

LoginPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object.isRequired,
  signUpError: PropTypes.object.isRequired,
  searchParams: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
